import { ChangeDetectionStrategy, Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
	LawFirm,
	LawFirmEmployee,
	LawFirmEmployeeType,
} from '@models/lawFirms';
import {
	ActingForType,
	BusinessMatterClientWithAssociations,
	MatterFileType,
	PersonMatterClientWithAssociations,
} from '@models/matter';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { MatterClients } from '@state/global/matter_clients';
import { MatterDispatch } from '@state/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { Observable } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { ICON } from '@core/util/icon';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';
import {
	EditClientModalComponent,
	EditClientModalData,
} from '../../fields/client/simple-edit/edit-modal/edit-modal.component';
import { CreateLawFirmEmployeeComponent } from '../../fields/law-firms/create-employee/create-employee.component';
import {
	clientKebabOptions,
	ClientKebabOptionTypes,
	contactCardDropdownItems,
	contactCardLawfirmDropdownItems,
} from './contacts.types';
import { ClientType } from '@models/clients';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { GlobalProfileComponent } from 'src/app/private/global-profile/global-profile.component';
import { TestHooks } from './contacts.hooks';

type MatterClientAssociations =
	| PersonMatterClientWithAssociations
	| BusinessMatterClientWithAssociations;

@Component({
	selector: 'app-matter-overview-contacts',
	templateUrl: './contacts.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class ContactsComponent {
	testHooks = TestHooks;
	public clientKebabOptions = clientKebabOptions;
	public contactCardDropdownItems = contactCardDropdownItems;
	public contactCardLawfirmDropdownItems = contactCardLawfirmDropdownItems;
	public ICON = ICON;
	public actingForType = ActingForType;
	public matterFileType = MatterFileType;
	public clientTypes = ClientType;
	@Select(MatterClients.get_matterClientsWithAssociationMapped)
	clients$: Observable<MatterClientAssociations[]>;
	@Select(MatterClients.get_otherPartyWithAssociationMapped)
	otherParties$: Observable<LawFirm>;

	@Select(SelectedMatter.otherPartyLawFirm) lawFirm$: Observable<LawFirm>;
	@Select(SelectedMatter.showOtherPartyCardsOverview)
	showOtherPartyCardsOverview$: Observable<boolean>;

	@ViewSelectSnapshot(SelectedMatter.partiesClientsUrl) clientsUrl: string;
	@ViewSelectSnapshot(SelectedMatter.id) matterId: string;

	constructor(
		private navService: NavigationService,
		private matDialog: MatDialog,
		private matterDispatch: MatterDispatch,
		private drawerService: DrawerService,
	) {}

	clientKebebSelect(
		ev: DropdownResult<DropdownOption<ClientKebabOptionTypes>>,
		id: string,
	): void {
		if (ev.result.name === ClientKebabOptionTypes.GLOBAL) {
			this.drawerService.openDrawer(GlobalProfileComponent, {
				title: 'Global Profile',
				data: { clientId: id },
			});
		} else if (ev.result.name === ClientKebabOptionTypes.QUICK_EDIT) {
			this.matDialog.open<EditClientModalComponent, EditClientModalData>(
				EditClientModalComponent,
				{ data: { id } },
			);
		}
	}

	clientKebebSelectLawfirm(
		ev: DropdownResult<DropdownOption<ClientKebabOptionTypes>>,
		role: LawFirmEmployeeType,
	): void {
		if (ev.result.name === ClientKebabOptionTypes.QUICK_EDIT) {
			// @@TODO: this needs to get moved into a dispatched action
			this.lawFirm$
				.pipe(
					take(1),
					switchMap((lawFirm) =>
						this.matDialog
							.open(CreateLawFirmEmployeeComponent, {
								data: {
									lawFirm,
									role,
									firstName: lawFirm?.[role]?.firstName,
									lastName: lawFirm?.[role]?.lastName,
								},
							})
							.afterClosed()
							.pipe(
								map(
									(employee) =>
										[employee, lawFirm] as [LawFirmEmployee, LawFirm],
								),
							),
					),
					// note, untilDestroyed is intentionally ignored here
				)
				.subscribe(([employee, lawFirm]) => {
					if (employee) {
						employee.role = role;
						this.matterDispatch.CreateLawFirmEmployee(
							this.matterId,
							employee,
							lawFirm?.id,
						);
					}
				});
		}
	}

	getLawyerAndClerkCount(lawFirm: LawFirm): number {
		return (lawFirm?.lawyer ? 1 : 0) + (lawFirm?.clerk ? 1 : 0);
	}

	// If the grid item count is >= 3, the sizing is different
	getContainerCSSClass(numberOfItems: number): string {
		let containerClass = 'card-container--grid';

		if (numberOfItems >= 3) {
			containerClass += ` ${containerClass}-full`;
		} else if (numberOfItems > 0) {
			containerClass += ` ${containerClass}-${numberOfItems}-items`;
		}

		return containerClass;
	}

	trackByClientId(_: number, item: MatterClientAssociations): string {
		return item.id;
	}
}
